import { runtimeConfig } from "@/runtime-config";
import { setWorkspaceIntegrations } from "@/store/integrations/integrations-slice";
import { setProjectUsers } from "@/store/project-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  initCompanyFeaturesAvailable,
  initFeaturesAvailable,
} from "@/store/subscription-features/subscription-features-slice";
import { setCompanyRole, setProjectRole } from "@/store/user-slice";
import { GUID } from "@faro-lotv/foundation";
import {
  setCompanyId,
  setDashboardUrl,
  setProjectAccessLevel,
  setProjectArchivalState,
  setProjectName,
  setProjectOriginalId,
} from "@faro-lotv/project-source";
import {
  parseGenericUserInfoFromUserInfo,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useEffect, useState } from "react";
import { useProjectLoadingStore } from "./project-loading-store";

/**
 * Updates the project metadata from the CoreAPI:
 * * feature-slice with the user's permissions specific to the loaded project
 * * company id for this project
 *
 * @param projectId the currently loaded project
 * @returns true when the loading finished and all the metadata is available
 */
export function useUpdateProjectMetadata(projectId: GUID): boolean {
  const dispatch = useAppDispatch();
  const { mustLoadProjectMetadata, projectMetadataLoaded } =
    useProjectLoadingStore();

  const { coreApiClient } = useApiClientContext();

  const [isReady, setIsReady] = useState(false);

  // Update feature slice with the user's permissions for the loaded project
  useEffect(() => {
    if (!mustLoadProjectMetadata(projectId)) {
      setIsReady(true);
      return;
    }

    async function updateMetaData(): Promise<void> {
      if (projectId) {
        const contextPromise = coreApiClient.getProjectContext(projectId);
        const [
          context,
          companyContext,
          members,
          project,
          users,
          usersTokens,
          extra,
        ] = await Promise.all([
          contextPromise,
          // Fails for users without access to the company, e.g. when opening a public project
          contextPromise.then((context) =>
            coreApiClient
              .getCompanyContext(context.company.id)
              .catch(() => undefined),
          ),
          contextPromise.then((context) =>
            coreApiClient
              .getProjectMembers(context.company.id, projectId)
              // The response is not a valid ProjectMembersPayload
              // Since the endpoint is mostly used for fetching the profile images of the members,
              // we can safely ignore the error and return undefined
              .catch(() => undefined),
          ),
          coreApiClient.getProjectInfo(projectId),
          // getProjectUsers and getUsersTokens fails for not logged in user
          // just return nothing or undefined if they fail
          coreApiClient
            .getProjectUsers(projectId)
            .catch(() => ({ data: { users: [] } })),
          coreApiClient.getUsersTokens().catch(() => undefined),
          coreApiClient
            .getExtendedProjectResponse(projectId)
            .catch(() => undefined),
        ]);

        const workspaceUrl = `${runtimeConfig.externalLinks.dashboardUrl}/${context.company.id}`;
        dispatch(initFeaturesAvailable(context.featuresAvailable));
        dispatch(
          initCompanyFeaturesAvailable(companyContext?.featuresAvailable ?? []),
        );
        dispatch(setWorkspaceIntegrations(usersTokens));
        dispatch(setCompanyId(context.company.id));
        dispatch(setCompanyRole(context.company.role));
        dispatch(setProjectRole(context.role));
        dispatch(setProjectName(project.name));
        dispatch(setProjectAccessLevel(project.accessLevel));
        dispatch(setProjectArchivalState(project.archivingState));
        dispatch(setDashboardUrl(workspaceUrl));
        dispatch(
          setProjectUsers(
            users.data.users
              .map(parseGenericUserInfoFromUserInfo)
              // Add the profileImageUrl to the users if it's missing using the members data
              .map((user) => {
                if (!user.profileImageUrl) {
                  const member = members?.data.projectMembers.find(
                    (member) => member.identity === user.identity,
                  );
                  user.profileImageUrl = member?.thumbnailUrl;
                }
                return user;
              }),
          ),
        );
        dispatch(setProjectOriginalId(extra?.project.originalProjectId));

        setIsReady(true);
        projectMetadataLoaded(projectId);
      }
    }

    updateMetaData();
  }, [
    coreApiClient,
    dispatch,
    projectId,
    projectMetadataLoaded,
    mustLoadProjectMetadata,
  ]);

  return isReady;
}
